/**
 * ============================================================================
 * DELIVERY TRACKING COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Visual timeline of delivery progress
 */

import React from 'react';
import { useI18n } from '../../providers';
import Card from '../common/Card';

/**
 * Delivery Tracking Component
 * @param {Object} props
 * @param {Object} props.transaction - Transaction object
 * @returns {JSX.Element}
 */
const DeliveryTracking = ({ transaction }) => {
  const { formatDate, formatTime, language } = useI18n();

  if (!transaction) return null;

  const getEventIcon = (status) => {
    const icons = {
      created: '📝',
      link_sent: '📤',
      payment_pending: '💳',
      escrow_funded: '💰',
      in_progress: '🚚',
      delivered: '✅',
      completed: '🎉',
      disputed: '⚠️',
    };
    return icons[status] || '📋';
  };

  const getEventColor = (status) => {
    const colors = {
      created: 'from-gray-500 to-slate-500',
      link_sent: 'from-blue-500 to-cyan-500',
      payment_pending: 'from-yellow-500 to-orange-500',
      escrow_funded: 'from-green-500 to-emerald-500',
      in_progress: 'from-blue-500 to-indigo-500',
      delivered: 'from-green-500 to-teal-500',
      completed: 'from-purple-500 to-pink-500',
      disputed: 'from-red-500 to-pink-500',
    };
    return colors[status] || 'from-gray-500 to-slate-500';
  };

  const getEventLabel = (status) => {
    const labels = {
      created: language === 'fr' ? 'Transaction créée' : 'Transaction Created',
      link_sent: language === 'fr' ? 'Lien partagé' : 'Link Sent',
      payment_pending: language === 'fr' ? 'Paiement en attente' : 'Payment Pending',
      escrow_funded: language === 'fr' ? 'Fonds en escrow' : 'Funds in Escrow',
      in_progress: language === 'fr' ? 'Livraison en cours' : 'Delivery In Progress',
      delivered: language === 'fr' ? 'Livré' : 'Delivered',
      completed: language === 'fr' ? 'Complété' : 'Completed',
      disputed: language === 'fr' ? 'En litige' : 'Disputed',
    };
    return labels[status] || status;
  };

  // Build timeline events
  const timelineEvents = [];

  if (transaction.createdAt) {
    timelineEvents.push({
      type: 'created',
      date: transaction.createdAt,
      completed: true,
    });
  }

  if (transaction.status === 'link_sent' || transaction.status !== 'created') {
    timelineEvents.push({
      type: 'link_sent',
      date: transaction.updatedAt || transaction.createdAt,
      completed: ['link_sent', 'payment_pending', 'escrow_funded', 'in_progress', 'delivered', 'completed'].includes(
        transaction.status
      ),
    });
  }

  if (transaction.status === 'payment_pending' || transaction.status !== 'created' && transaction.status !== 'link_sent') {
    timelineEvents.push({
      type: 'payment_pending',
      date: transaction.updatedAt,
      completed: ['payment_pending', 'escrow_funded', 'in_progress', 'delivered', 'completed'].includes(
        transaction.status
      ),
    });
  }

  if (transaction.status === 'escrow_funded' || transaction.paymentReceivedAt) {
    timelineEvents.push({
      type: 'escrow_funded',
      date: transaction.paymentReceivedAt || transaction.escrowFundedAt || transaction.updatedAt,
      completed: ['escrow_funded', 'in_progress', 'delivered', 'completed'].includes(transaction.status),
    });
  }

  if (transaction.status === 'in_progress' || transaction.status === 'delivered' || transaction.status === 'completed') {
    timelineEvents.push({
      type: 'in_progress',
      date: transaction.updatedAt,
      completed: ['in_progress', 'delivered', 'completed'].includes(transaction.status),
    });
  }

  if (transaction.deliveredAt || transaction.status === 'delivered' || transaction.status === 'completed') {
    timelineEvents.push({
      type: 'delivered',
      date: transaction.deliveredAt || transaction.updatedAt,
      completed: ['delivered', 'completed'].includes(transaction.status),
    });
  }

  if (transaction.status === 'completed') {
    timelineEvents.push({
      type: 'completed',
      date: transaction.updatedAt,
      completed: true,
    });
  }

  if (timelineEvents.length === 0) {
    return (
      <Card variant="glass" className="p-12 text-center">
        <p className="text-gray-500 dark:text-gray-400">
          {language === 'fr' ? 'Aucun événement' : 'No events yet'}
        </p>
      </Card>
    );
  }

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <h3 className="text-xl font-black text-gray-900 dark:text-white mb-6 flex items-center gap-2">
        <span className="text-2xl">📊</span>
        {language === 'fr' ? 'Suivi de livraison' : 'Delivery Tracking'}
      </h3>
      <div className="space-y-4">
        {timelineEvents.map((event, index) => (
          <div
            key={index}
            className="flex items-start gap-4 animate-fadeInUp relative"
            style={{ animationDelay: `${index * 0.1}s` }}
          >
            {/* Timeline Line */}
            {index < timelineEvents.length - 1 && (
              <div
                className={`absolute left-6 top-12 bottom-0 w-0.5 ${
                  event.completed
                    ? 'bg-gradient-to-b from-green-500 to-blue-500'
                    : 'bg-gray-300 dark:bg-gray-600'
                }`}
              ></div>
            )}

            {/* Icon */}
            <div
              className={`w-12 h-12 rounded-xl bg-gradient-to-br ${getEventColor(
                event.type
              )} flex items-center justify-center text-xl shadow-lg flex-shrink-0 relative z-10 ${
                event.completed ? '' : 'opacity-50'
              }`}
            >
              {getEventIcon(event.type)}
            </div>

            {/* Content */}
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-black text-gray-900 dark:text-white">
                  {getEventLabel(event.type)}
                </h4>
                {event.completed && (
                  <span className="text-xs font-bold text-green-600 dark:text-green-400 bg-green-100 dark:bg-green-900/30 px-2 py-1 rounded-lg">
                    {language === 'fr' ? 'Terminé' : 'Completed'}
                  </span>
                )}
              </div>
              {event.date && (
                <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>{formatDate(event.date)}</span>
                  <span>•</span>
                  <span>{formatTime(event.date)}</span>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
};

export default DeliveryTracking;
